<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { messageStamp } from 'shared/helpers/timeHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CallStatusBadge from 'dashboard/components-next/Calls/CallStatusBadge.vue';
import {
  getCallKind,
  formatCallDuration,
} from 'dashboard/components-next/Calls/constants';
import SaasCallsAPI from 'dashboard/api/saas/calls';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();

const calls = ref([]);
const loading = ref(true);
const error = ref('');

const fetchCalls = async () => {
  if (!props.contactId) return;

  try {
    loading.value = true;
    error.value = '';
    const response = await SaasCallsAPI.getContactCalls(props.contactId);
    calls.value = response.data.payload || [];
  } catch (e) {
    error.value =
      e.response?.data?.error || t('CALLS_PAGE.SIDEBAR_LIST.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};

const conversationRoute = call => ({
  name: 'inbox_conversation',
  params: {
    accountId: route.params.accountId,
    conversation_id: call.conversation.display_id,
  },
  query: { messageId: call.message_id },
});

watch(
  () => props.contactId,
  () => {
    fetchCalls();
  },
  { immediate: true }
);
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="loading" class="flex justify-center items-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>
    <div v-else-if="error" class="text-center text-n-ruby-11 text-xs">
      {{ error }}
    </div>
    <div v-else-if="!calls.length" class="text-center text-n-slate-11 text-xs">
      {{ t('CALLS_PAGE.SIDEBAR_LIST.EMPTY') }}
    </div>
    <div v-else class="flex flex-col">
      <router-link
        v-for="call in calls"
        :key="call.id"
        :to="conversationRoute(call)"
        class="flex items-center gap-2 py-2 px-1 border-b border-n-weak last:border-0 rounded-md hover:bg-n-alpha-1 min-w-0"
      >
        <CallStatusBadge :kind="getCallKind(call)" class="shrink-0" />
        <span
          v-if="call.duration_seconds"
          class="text-xs text-n-slate-11 tabular-nums shrink-0"
        >
          {{ formatCallDuration(call.duration_seconds) }}
        </span>
        <Icon
          v-if="call.recording_url"
          v-tooltip.top="t('CALLS_PAGE.SIDEBAR_LIST.HAS_RECORDING')"
          icon="i-lucide-mic"
          class="size-3.5 text-n-slate-10 shrink-0"
        />
        <span
          class="ms-auto text-xs text-n-slate-10 tabular-nums shrink-0 truncate"
        >
          {{ messageStamp(call.created_at, 'MMM d, h:mm a') }}
        </span>
      </router-link>
    </div>
  </div>
</template>
